import { Component, OnInit, OnDestroy, Output, EventEmitter, ViewChild, ElementRef, NgZone, Input } from '@angular/core';
import { MapToolsService } from 'src/app/shared/services/map-tools.service';
import { AuthenticationService } from 'src/app/services/auth/authentication.service';

declare var google: any;

@Component({
  selector: 'app-tools-buscador',
  templateUrl: './tools-buscador.component.html',
  styleUrls: ['./tools-buscador.component.scss']
})
export class ToolsBuscadorComponent implements OnInit, OnDestroy {

  @Output() eventClose = new EventEmitter();
  @Output() irALugar = new EventEmitter();
  @ViewChild('searchInput', { static: false }) searchInput: ElementRef;
  @Input() map: any; // Referencia al mapa de Google

  searchQuery: string = '';
  autocomplete: any;
  predictions: any[] = [];
  isLoading: boolean = false;
  selectedPlace: any = null;
  showPredictions: boolean = false;
  searchMarker: any = null; // Marcador de búsqueda
  
  // 💰 SESSION TOKEN para optimizar costos de Places API
  // Reduce costos en ~85% ($150 USD/mes de ahorro)
  private sessionToken: any = null;
  
  // 🔒 SEGURIDAD: Timer para limpiar tokens viejos (evita cobros extras)
  private sessionTokenTimer: any = null;
  private readonly SESSION_TOKEN_TIMEOUT = 170000; // 2min 50seg (antes de 3min de Google)
  
  // 🔒 LÍMITE DE BÚSQUEDAS: Control por sesión
  busquedasRestantes: number = 10;
  limiteBusquedas: number = 10;
  mostrarLimiteAlcanzado: boolean = false;

  constructor(
    private maptoolService: MapToolsService,
    private ngZone: NgZone,
    private authService: AuthenticationService
  ) { }

  ngOnInit() {
    // Intentar inicializar inmediatamente
    this.initAutocomplete();
    
    // Si no está disponible, reintentar después de un momento
    if (!this.autocomplete) {
      setTimeout(() => {
        this.initAutocomplete();
      }, 1000);
    }
    
    // 🔒 Inicializar contador de búsquedas
    this.actualizarContadorBusquedas();
  }

  /**
   * 🔒 CRÍTICO: Limpia Session Token al destruir componente
   * Previene cobros extras cuando:
   * - Usuario cierra sesión
   * - Usuario navega a otra página
   * - Usuario cambia de tab
   */
  ngOnDestroy() {
    this.limpiarSessionToken('ngOnDestroy - componente destruido');
    
    // Limpiar también el marcador del mapa
    if (this.searchMarker) {
      try {
        this.searchMarker.setMap(null);
      } catch (e) {
        console.warn('Error al limpiar marcador en ngOnDestroy:', e);
      }
    }
    
    console.log('🧹 Componente tools-buscador destruido y recursos limpiados');
  }

  /**
   * Inicializa el servicio de autocompletado de Google Places
   */
  initAutocomplete() {
    if (typeof google !== 'undefined' && google.maps && google.maps.places) {
      try {
        // Crear una instancia del servicio AutocompleteService
        this.autocomplete = new google.maps.places.AutocompleteService();
        console.log('✅ Servicio de Google Places Autocomplete inicializado correctamente');
      } catch (error) {
        console.error('❌ Error al inicializar Google Places Autocomplete:', error);
      }
    } else {
      console.warn('⚠️ Google Maps o Places API no están disponibles todavía');
    }
  }

  /**
   * Se ejecuta cuando el usuario escribe en el buscador
   * 💰 OPTIMIZADO con Session Tokens (ahorro: ~$150 USD/mes)
   * 🔒 LIMITADO: 10 búsquedas por sesión
   */
  onSearchChange(query: string) {
    this.searchQuery = query;
    
    if (query.length < 2) {
      this.predictions = [];
      this.showPredictions = false;
      this.mostrarLimiteAlcanzado = false;
      return;
    }

    // 🔒 VERIFICAR LÍMITE DE BÚSQUEDAS
    if (!this.authService.puedeBuscar()) {
      console.error('🚫 Límite de búsquedas alcanzado');
      this.predictions = [];
      this.showPredictions = false;
      this.mostrarLimiteAlcanzado = true;
      this.isLoading = false;
      return;
    }

    // Verificar que el servicio esté inicializado
    if (!this.autocomplete) {
      console.warn('⚠️ Servicio no inicializado, intentando reinicializar...');
      this.initAutocomplete();
      if (!this.autocomplete) {
        console.error('❌ No se pudo inicializar el servicio de autocompletado');
        this.isLoading = false;
        return;
      }
    }

    // 💰 CREAR SESSION TOKEN si no existe (inicio de sesión de búsqueda)
    if (!this.sessionToken && typeof google !== 'undefined' && google.maps && google.maps.places) {
      this.sessionToken = new google.maps.places.AutocompleteSessionToken();
      console.log('🔑 Session Token creado (optimización de costos)');
      
      // 🔒 SEGURIDAD: Timer para auto-limpiar token después de 2min 50seg
      // Evita que token expire (3min) y Google cobre doble
      this.iniciarTimerSessionToken();
    }

    this.isLoading = true;
    this.showPredictions = true;

    // Configuración con SESSION TOKEN para reducir costos
    const request: any = {
      input: query,
      componentRestrictions: { country: 'py' },
      sessionToken: this.sessionToken // 💰 TOKEN para combinar autocomplete + details en 1 cargo
    };

    console.log('🔍 Realizando búsqueda con Session Token:', query);

    // Obtener predicciones
    this.autocomplete.getPlacePredictions(request, (predictions: any, status: any) => {
      this.ngZone.run(() => {
        this.isLoading = false;
        
        console.log('📡 Status de la API:', status);
        console.log('📍 Predicciones recibidas:', predictions ? predictions.length : 0);
        
        if (status === 'OK' && predictions && predictions.length > 0) {
          this.predictions = predictions;
          console.log('✅ Resultados encontrados:', this.predictions.length);
          
          // 🔒 INCREMENTAR CONTADOR de búsquedas (búsqueda exitosa)
          this.authService.incrementarBusqueda();
          this.actualizarContadorBusquedas();
        } else if (status === 'ZERO_RESULTS') {
          console.warn('⚠️ No se encontraron resultados para:', query);
          this.predictions = [];
          // 🔒 Limpiar token si no hay resultados (usuario no podrá seleccionar nada)
          this.limpiarSessionToken('ZERO_RESULTS - sin resultados');
        } else if (status === 'REQUEST_DENIED') {
          console.error('❌ REQUEST_DENIED - Verifica que Places API esté habilitada en Google Cloud Console');
          console.error('❌ O que tu Client ID tenga permisos para Places API');
          this.predictions = [];
          // 🔒 Limpiar token en caso de error
          this.limpiarSessionToken('REQUEST_DENIED - error de permisos');
        } else if (status === 'OVER_QUERY_LIMIT') {
          console.error('❌ OVER_QUERY_LIMIT - Has excedido el límite de consultas');
          this.predictions = [];
          // 🔒 Limpiar token en caso de error
          this.limpiarSessionToken('OVER_QUERY_LIMIT - límite excedido');
        } else {
          console.error('❌ Error en búsqueda. Status:', status);
          this.predictions = [];
          // 🔒 Limpiar token en caso de error desconocido
          this.limpiarSessionToken('ERROR - ' + status);
        }
      });
    });
  }

  /**
   * Se ejecuta cuando el usuario selecciona una predicción
   * 💰 OPTIMIZADO: Usa el mismo Session Token para combinar el costo
   */
  selectPrediction(prediction: any) {
    console.log('📍 Lugar seleccionado:', prediction.description);
    
    this.searchQuery = prediction.description;
    this.showPredictions = false;
    
    // Obtener detalles del lugar usando PlacesService
    const service = new google.maps.places.PlacesService(document.createElement('div'));
    
    console.log('🔍 Obteniendo detalles del lugar con Session Token...');
    
    service.getDetails({
      placeId: prediction.place_id,
      fields: ['name', 'geometry', 'formatted_address', 'address_components'],
      sessionToken: this.sessionToken // 💰 MISMO TOKEN = 1 solo cargo (autocomplete + details)
    }, (place: any, status: any) => {
      this.ngZone.run(() => {
        console.log('📡 Status de getDetails:', status);
        
        if (status === 'OK' && place) {
          this.selectedPlace = place;
          
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          
          console.log('✅ Coordenadas obtenidas:', { lat, lng });
          
          // Crear marcador en el mapa
          this.addSearchMarker(lat, lng, place.name || place.formatted_address);
          
          // Emitir evento para centrar el mapa
          const position = {
            latitud: lat,
            longitud: lng,
            nombre: place.name || place.formatted_address
          };
          
          console.log('📤 Emitiendo evento para centrar mapa');
          this.irALugar.emit(position);
          
          // 💰 IMPORTANTE: Limpiar Session Token después de Place Details
          // Esto finaliza la sesión y Google cobra solo UNA VEZ por todo
          this.limpiarSessionToken('Place Details completado - sesión finalizada');
        } else {
          console.error('❌ Error al obtener detalles:', status);
          // 🔒 Limpiar token también en caso de error
          this.limpiarSessionToken('Error en Place Details - ' + status);
        }
      });
    });
  }

  /**
   * Agrega un marcador en el mapa en la ubicación buscada
   */
  addSearchMarker(lat: number, lng: number, title: string) {
    console.log('🔴 === INICIANDO CREACIÓN DE PIN ===');
    console.log('📍 Coordenadas:', { lat, lng });
    console.log('📝 Título:', title);
    
    // Eliminar marcador anterior si existe
    if (this.searchMarker) {
      console.log('🗑️ Eliminando pin anterior');
      try {
        this.searchMarker.setMap(null);
      } catch (e) {
        console.warn('Error al eliminar pin anterior:', e);
      }
      this.searchMarker = null;
    }

    // Obtener mapa desde el servicio
    const mapInstance = this.maptoolService.getMapa();
    
    if (!mapInstance) {
      console.error('❌❌❌ CRÍTICO: No hay referencia al mapa');
      console.error('Verifica que mapa-full.component esté guardando la referencia');
      alert('ERROR: No se puede crear el pin. Recarga la página.');
      return;
    }

    console.log('✅ Mapa obtenido correctamente');
    
    if (!google || !google.maps) {
      console.error('❌ Google Maps no disponible');
      return;
    }

    try {
      console.log('🎯 Creando marcador...');
      
      // Crear marcador GRANDE y VISIBLE
      this.searchMarker = new google.maps.Marker({
        position: { lat: lat, lng: lng },
        map: mapInstance,
        title: title,
        animation: google.maps.Animation.DROP,
        icon: {
          url: 'http://maps.google.com/mapfiles/ms/icons/red-dot.png',
          scaledSize: new google.maps.Size(60, 60) // PIN MUY GRANDE
        },
        zIndex: 999999
      });

      console.log('✅✅✅ PIN CREADO EXITOSAMENTE!');
      console.log('📌 Marcador:', this.searchMarker);

      // InfoWindow mejorado
      const infoWindow = new google.maps.InfoWindow({
        content: `
          <div style="padding: 15px; max-width: 300px; font-family: Arial;">
            <h2 style="margin: 0 0 12px 0; color: #667eea; font-size: 20px;">
              📍 ${title}
            </h2>
            <div style="background: #f5f5f5; padding: 10px; border-radius: 6px; margin: 10px 0;">
              <p style="margin: 5px 0; font-size: 14px;"><strong>Lat:</strong> ${lat.toFixed(6)}</p>
              <p style="margin: 5px 0; font-size: 14px;"><strong>Lng:</strong> ${lng.toFixed(6)}</p>
            </div>
            <div style="background: #4caf50; color: white; padding: 10px; border-radius: 6px; text-align: center;">
              <strong>✅ UBICACIÓN ENCONTRADA</strong>
            </div>
          </div>
        `
      });

      // Click en marcador
      this.searchMarker.addListener('click', () => {
        infoWindow.open(mapInstance, this.searchMarker);
      });

      // Abrir automáticamente
      setTimeout(() => {
        infoWindow.open(mapInstance, this.searchMarker);
        console.log('📝 InfoWindow mostrado');
      }, 500);

      // Animación bouncing por 2 segundos
      this.searchMarker.setAnimation(google.maps.Animation.BOUNCE);
      setTimeout(() => {
        if (this.searchMarker) {
          this.searchMarker.setAnimation(null);
          console.log('⏹️ Animación detenida');
        }
      }, 2100);

      console.log('🎉🎉🎉 PIN COMPLETAMENTE CONFIGURADO');
      
    } catch (error) {
      console.error('❌❌❌ ERROR AL CREAR PIN:', error);
      alert('Error al crear el pin: ' + error.message);
    }
  }


  /**
   * Limpia el buscador
   */
  clearSearch() {
    this.searchQuery = '';
    this.predictions = [];
    this.showPredictions = false;
    this.selectedPlace = null;
    
    // 🔒 Limpiar Session Token (usuario canceló sin seleccionar)
    this.limpiarSessionToken('clearSearch - usuario limpió búsqueda');
    
    // Eliminar marcador del mapa
    if (this.searchMarker) {
      this.searchMarker.setMap(null);
      this.searchMarker = null;
    }
  }

  /**
   * Cierra el panel de búsqueda
   */
  close(event?: Event) {
    // 🔒 CRÍTICO: Limpiar Session Token al cerrar panel
    // Previene cobros extras si usuario cierra sin seleccionar
    this.limpiarSessionToken('close - usuario cerró panel');
    
    this.eventClose.emit(event);
  }

  /**
   * Maneja el foco del input
   */
  onFocus() {
    if (this.predictions.length > 0) {
      this.showPredictions = true;
    }
  }

  /**
   * Maneja la pérdida de foco del input
   */
  onBlur() {
    // Pequeño delay para permitir clicks en las predicciones
    setTimeout(() => {
      this.showPredictions = false;
    }, 200);
  }

  // ==================== 🔒 MÉTODOS DE SEGURIDAD PARA SESSION TOKEN ====================

  /**
   * 🔒 Inicia timer de auto-limpieza del Session Token
   * Previene que el token expire (3min) y Google cobre doble
   */
  private iniciarTimerSessionToken(): void {
    // Limpiar timer anterior si existe
    if (this.sessionTokenTimer) {
      clearTimeout(this.sessionTokenTimer);
    }
    
    // Nuevo timer: limpia token después de 2min 50seg
    this.sessionTokenTimer = setTimeout(() => {
      this.limpiarSessionToken('TIMEOUT - token expiraría en 10 segundos');
    }, this.SESSION_TOKEN_TIMEOUT);
    
    console.log(`⏰ Timer de seguridad iniciado (${this.SESSION_TOKEN_TIMEOUT / 1000}s)`);
  }

  /**
   * 🔒 MÉTODO CENTRALIZADO para limpiar Session Token
   * Se debe llamar en TODOS los escenarios de salida:
   * - Usuario selecciona lugar (éxito)
   * - Usuario cierra panel (cancelación)
   * - Usuario limpia búsqueda
   * - Error en API
   * - Timeout de 3 minutos
   * - Componente se destruye
   */
  private limpiarSessionToken(razon: string): void {
    if (this.sessionToken) {
      this.sessionToken = null;
      console.log(`🔓 Session Token limpiado: ${razon}`);
    }
    
    // Limpiar timer de seguridad
    if (this.sessionTokenTimer) {
      clearTimeout(this.sessionTokenTimer);
      this.sessionTokenTimer = null;
      console.log('⏰ Timer de seguridad cancelado');
    }
  }

  // ==================== 🔒 CONTROL DE LÍMITE DE BÚSQUEDAS ====================

  /**
   * Actualiza el contador de búsquedas desde el AuthenticationService
   */
  private actualizarContadorBusquedas(): void {
    this.busquedasRestantes = this.authService.getBusquedasRestantes();
    this.limiteBusquedas = this.authService.getLimiteBusquedas();
    
    console.log(`📊 Búsquedas restantes: ${this.busquedasRestantes}/${this.limiteBusquedas}`);
  }

}

